import { type ReactNode } from 'react'

import { usePage } from '@inertiajs/react'

import { type GlobalPageProps } from 'helpers/interfaces'

import { ThemeProvider } from '@oxfordmedicalsimulation/oms-fe-components'

import { standaloneTheme } from './standaloneTheme'

type StandaloneThemeProviderProps = {
  children: ReactNode
}

/**
 * Conditional theme provider for standalone (VR headset) mode.
 *
 * Wraps children in ThemeProvider with standalone theme when runtime.standalone is true,
 * otherwise renders children directly to inherit the parent theme from BaseLayout.
 *
 * Use this wrapper around specific pages/components that should have VR-optimized styling
 * (larger fonts, thicker borders, etc.) when running in standalone mode.
 *
 * @example
 * ```tsx
 * export const Feedback = (props) => {
 *   return (
 *     <StandaloneThemeProvider>
 *       <Page.Title title="Feedback" />
 *       {// ... rest of component}
 *     </StandaloneThemeProvider>
 *   )
 * }
 * ```
 */
export const StandaloneThemeProvider = ({ children }: StandaloneThemeProviderProps) => {
  const {
    props: { runtime },
  } = usePage<GlobalPageProps>()

  const isStandalone = runtime?.standalone ?? false

  // Only wrap in ThemeProvider if we need the standalone theme
  // Otherwise, render children directly to inherit parent theme
  if (!isStandalone) return <>{children}</>

  return <ThemeProvider theme={standaloneTheme}>{children}</ThemeProvider>
}
